import { useMemo } from "react";
import { useFormikContext } from "formik";
import { useQuery } from "@tanstack/react-query";
import { getRFQId } from "actions/RFQ/rfqsAction";

export interface RfxParticipant {
	email: string;
	name: string;
	type: "supplier" | "guest";
}

interface RfxParticipantsFormValues {
	guest?: { email: string; name: string }[];
	suppliers?: number[];
}

/**
 * Resolves the suppliers/guests currently selected on the RFx Participants step
 * into a display-ready list of { name, email }.
 *
 * `values.suppliers` only carries company ids — their name/email come from the
 * same `["rfq", rfxId]` query the Participants step (Suppliers.js) already
 * populates, so this reads from the react-query cache rather than re-fetching.
 * `values.guest` already carries name/email directly (see generateGuestSchema).
 */
export const useSelectedRfxParticipants = (
	rfxId: number | string | null | undefined
): RfxParticipant[] => {
	const { values } = useFormikContext<RfxParticipantsFormValues>();
	const { data: rfqData } = useQuery({
		queryKey: ["rfq", rfxId],
		queryFn: () => getRFQId(rfxId),
		enabled: !!rfxId,
		staleTime: 30000,
	});

	return useMemo(() => {
		const supplierRecords: { email: string; id: number; name: string }[] =
			rfqData?.supplierList?.suppliers ?? [];
		const selectedSupplierIds = values?.suppliers ?? [];

		const suppliers: RfxParticipant[] = selectedSupplierIds
			.map((id) => supplierRecords.find((supplier) => supplier.id === id))
			.filter((supplier): supplier is { email: string; id: number; name: string } =>
				Boolean(supplier)
			)
			.map((supplier) => ({
				type: "supplier" as const,
				name: supplier.name,
				email: supplier.email,
			}));

		const guests: RfxParticipant[] = (values?.guest ?? []).map((guest) => ({
			type: "guest" as const,
			name: guest.name,
			email: guest.email,
		}));

		return [...suppliers, ...guests];
	}, [rfqData, values?.suppliers, values?.guest]);
};
